import { ElementRef, Injectable, NgZone } from '@angular/core';

export class FormErrorScrollingOptions {
  isHostScrollable?: boolean;
  isHostMatTable?: boolean;
  isHostMatStep?: boolean;
}

const ERROR_MESSAGE_SELECTOR = '.form-error, mat-error, .field-container.error';
const INVALID_CONTROL_SELECTOR = '.ng-invalid';
// Fallback cleanup delay for when the IntersectionObserver never reports the anchor,
// e.g. the scroll target turned out to have no layout box.
const ANCHOR_CLEANUP_DELAY = 2000;

@Injectable({ providedIn: 'root' })
export class FormErrorScrollingService {
  private readonly defaultView: HTMLElement;

  constructor(private ngZone: NgZone) {
    this.defaultView = window.document.body;
  }

  public scrollToFirstInvalidControl() {
    this.scrollToFirstInvalidControlInView(this.defaultView);
  }

  public scrollToFirstInvalidControlInView(
    view: ElementRef<HTMLElement> | HTMLElement,
    async: boolean = true,
    options?: FormErrorScrollingOptions
  ) {
    const hostElement = view instanceof HTMLElement ? view : view.nativeElement;
    if (async) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.scrollToFirstInvalidControlInViewSync(hostElement, options);
        });
      });
    } else {
      this.scrollToFirstInvalidControlInViewSync(hostElement, options);
    }
  }

  private scrollToFirstInvalidControlInViewSync(
    hostElement: HTMLElement,
    options: FormErrorScrollingOptions = {}
  ) {
    const firstErrorElement = this.findFirstErrorElement(hostElement);

    if (firstErrorElement) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      const anchor = document.createElement('span');
      anchor.style.position = 'absolute';
      const firstErrorPosition = firstErrorElement.style.position;

      let cleanedUp = false;
      const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        anchor.remove();
        firstErrorElement.style.position = firstErrorPosition;
      };

      if (options.isHostScrollable) {
        if (options.isHostMatTable) {
          anchor.style.top = '-58px';
        } else if (options.isHostMatStep) {
          anchor.style.top = '-250px';
        } else {
          anchor.style.top = '-0px';
        }
        firstErrorElement.appendChild(anchor);
        firstErrorElement.style.position = 'relative';

        // The anchor can end up without a layout box (e.g. a zero-height target). Scrolling to it
        // would then silently do nothing, so fall back to scrolling the target itself.
        if (anchor.getClientRects().length === 0) {
          cleanup();
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      } else {
        anchor.style.top = `${firstErrorElement.getBoundingClientRect().top + scrollTop - 150}px`;
        document.body.appendChild(anchor);
      }
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });

      const intersectionObserver = new IntersectionObserver(entries => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          intersectionObserver.disconnect();
          cleanup();
        }
      });

      intersectionObserver.observe(anchor);

      // Guarantee the anchor never leaks, even if it is never reported as intersecting.
      setTimeout(() => {
        intersectionObserver.disconnect();
        cleanup();
      }, ANCHOR_CLEANUP_DELAY);
    }
  }

  /**
   * Prefers the first visible rendered error message. When a control is invalid but has no error
   * message rendered next to it (e.g. a checkbox sub-field whose mat-error is missing or not yet
   * projected), falls back to the invalid control itself so it can still be scrolled to.
   */
  private findFirstErrorElement(hostElement: HTMLElement): HTMLElement {
    const errorMessages = hostElement.querySelectorAll(ERROR_MESSAGE_SELECTOR);
    for (let i = 0; i < errorMessages.length; i++) {
      const errorMessage = errorMessages[i] as HTMLElement;
      if (this.isVisible(errorMessage)) return errorMessage;
    }

    const invalidControls = hostElement.querySelectorAll(INVALID_CONTROL_SELECTOR);
    for (let i = 0; i < invalidControls.length; i++) {
      const invalidControl = invalidControls[i] as HTMLElement;
      // Skip wrappers (form / formGroup / formArray hosts) so we land on the leaf control.
      if (invalidControl.querySelector(INVALID_CONTROL_SELECTOR)) continue;
      if (this.isVisible(invalidControl)) return invalidControl;
    }

    return null;
  }

  /**
   * Elements hidden by an ancestor are still matched by querySelectorAll. Scrolling to one does
   * nothing, so they must be skipped. Covers `display: none` (offsetParent), collapsed containers
   * such as an inactive mat-step (client rects) and inherited `visibility: hidden`.
   */
  private isVisible(element: HTMLElement): boolean {
    return (
      element.offsetParent !== null &&
      element.getClientRects().length > 0 &&
      window.getComputedStyle(element).visibility !== 'hidden'
    );
  }
}
